import { FleetAlarmsService } from "../services/fleet-alarms-service";
import { AlarmType, AlarmState, Alarm } from "../services/types/fleet-alarms-service.types";
import { filterArrayByImo, filterWeatherAlarms } from "../utils/alarm-utils";
import { VesselRank, FleetAlarm, AlarmRankWeight, AlarmEvents } from "./types/voy-fleet-alarm-model.types";
import { VesselsModel } from "./vessels-model";
import { VoyFleetAlarmModelBase } from "./voy-fleet-alarm-model";
import { VoyFleetNotificationModel } from "./voy-fleet-notification-model";

export class VoyFleetAlarmModelImpl extends VoyFleetAlarmModelBase {
   constructor() {
      super()

      this._fetchAlarms = this._fetchAlarms.bind(this);
      this._rankVesselsByAlarmType = this._rankVesselsByAlarmType.bind(this);
      this._fetchAlarmsForNotificationSender = this._fetchAlarmsForNotificationSender.bind(this);
      this._fetchAlarms()
      this._fetchAlarmsForNotificationSender()


      setInterval(this._fetchAlarms, 1000 * 60 * 2)
      setInterval(this._fetchAlarmsForNotificationSender, 1000 * 60 * 2)
   }

   private _vesselRanks = new Map<number, VesselRank>();
   private _alarmsByVessel = new Map<number, FleetAlarm[]>();
   private _alarmByID = new Map<number, FleetAlarm>();
   private _weatherAlarms: Alarm[] = []
   private _weatherAlarmsForNotificationSender: FleetAlarm[] = []

   public get getWeatherAlarms(): Alarm[] {
      return this._weatherAlarms;
   }

   public get getWeatherAlarmsForNotificationSender(): FleetAlarm[] {
      return this._weatherAlarmsForNotificationSender;
   }

   private _rankVesselsByAlarmType() {

      this._alarmsByVessel.forEach((alarms, imo) => {
         const alarmsSortedByStartDate = alarms
            .sort((a, b) => a.id === b.id ? 0 : new Date(b.startTs).getTime() - new Date(a.startTs).getTime());

         const latestAlarmTs = alarmsSortedByStartDate[0]?.startTs
         let rankWeight = 0;

         for (let i = 0; i < alarms.length; i++) {
            const alarm = alarms[i];
            let alarmRank = 0;

            if (alarm.alarmState === AlarmState.Acknowledged) {
               continue
            }
            
            if (alarm.alarmType === AlarmType.NoPlan) {
               rankWeight = AlarmRankWeight.NoPlan
               break;
            }
            switch (alarm.alarmType) {
               case AlarmType.HeavyWeather:
                  alarmRank = AlarmRankWeight.HeavyWeather;
                  break;
               case AlarmType.ParametricRoll:
                  alarmRank = AlarmRankWeight.ParametricRoll;
                  break;
               case AlarmType.WRSafetyAlert:
                  alarmRank = AlarmRankWeight.WRSafetyAlert;
                  break;
               case AlarmType.WRPerformanceAlert:
                  alarmRank = AlarmRankWeight.WRPerformanceAlert;
                  break;
               case AlarmType.WRReject:
                  alarmRank = AlarmRankWeight.WRReject;
                  break;
               case AlarmType.Stoppage:
                  alarmRank = AlarmRankWeight.Stoppage;
                  break;
               case AlarmType.Speed:
                  alarmRank = AlarmRankWeight.Speed;
                  break;
               case AlarmType.Crosstrack:
                  alarmRank = AlarmRankWeight.Crosstrack;
                  break;
               default:
                  alarmRank = 0;
            }

            // if the current alarm have been handled
            // reduce the score by 10, so the combined
            // scores cannot excede the lowsest active alarm score
            if (alarm.alarmState === AlarmState.InProgress) {
               alarmRank = Math.floor(alarmRank - 10);
            }
            rankWeight += alarmRank;
         }

         this._vesselRanks.set(imo, { latestAlarmTs, rankWeight });
      })
   }

   private _setWeatherAlarms(alarms: Alarm[]) {

      const weatherAlarms = filterWeatherAlarms(alarms);

      //check if the alarm is in local storage, avoid duplicacy
      const weatherAlarmsFromLocalStorage = localStorage.getItem('doItLaterAlarm');
      if(weatherAlarmsFromLocalStorage) {
         const alarmsFromLocalStorage = JSON.parse(weatherAlarmsFromLocalStorage);
         //Remove the accepted alarms from local storage and remove the items if it is snoozed for more than one hour
         let activeAlarms:Alarm[] = []
         const currentTime = new Date().getTime();
         if(!weatherAlarms.length) {
            localStorage.removeItem('doItLaterAlarm');
         }
         weatherAlarms.map((alarm,index)=>{
         let temp = alarmsFromLocalStorage.findIndex((a: any) => a.id === alarm.id)
         if(temp>-1){
            const snoozedTime = new Date(alarmsFromLocalStorage[temp].snoozedTime).getTime();
            const hoursDifference =(currentTime-snoozedTime)/(1000 * 60 * 60);
            if(hoursDifference < 1){
               activeAlarms.push(alarmsFromLocalStorage[temp])
            }
         }
         localStorage.setItem('doItLaterAlarm', JSON.stringify(activeAlarms))     
         })
         const alarmsToBeAdded = weatherAlarms.filter(alarm => {
         return !alarmsFromLocalStorage.some((a: any) => a.id === alarm.id)
      })

         this._weatherAlarms = alarmsToBeAdded; 
      } else {
         this._weatherAlarms = weatherAlarms;
      }

      return weatherAlarms

   }

   async _fetchAlarmsForNotificationSender(): Promise<void> {
      let alarms: Alarm[] = [];

      const includeLogs = true;
      const includeDetections = false
      const excludeStates = [AlarmState.Ended,AlarmState.Pending,AlarmState.Cancelled,AlarmState.Acknowledged];
      const alarmTypes = [AlarmType.HeavyWeather, AlarmType.ParametricRoll];

      try {
         alarms = await FleetAlarmsService.getAlarms(null, includeLogs, includeDetections, excludeStates, undefined, alarmTypes);
         
         const weatherNotificationAlarms = filterArrayByImo(alarms)
         this._weatherAlarmsForNotificationSender = await this._enrichAlarmLogsWithUsers(weatherNotificationAlarms)

         if(this._weatherAlarmsForNotificationSender) {
            this.dispatchEvent(new Event(AlarmEvents.NotificationSenderAlarmsUpdated));
         }
         
      } catch(err) {
         console.error(err);
         return;
      }
   }

   private async _fetchAlarms(): Promise<void> {
      let alarms: Alarm[] = [];

      const includeLogs = false;
      const includeDetections = false
      const excludeStates = [AlarmState.Ended,AlarmState.Pending,AlarmState.Cancelled,AlarmState.Acknowledged];

      try {
         await VesselsModel?.refreshVessels()
         alarms = await FleetAlarmsService.getAlarms(null, includeLogs, includeDetections, excludeStates);
         this._setWeatherAlarms(alarms);
         alarms = this.removeDuplicateWeatherAlarms(alarms)
      } catch(err) {
         console.error(err);
         return;
      }
   
      const updatedAlarmsByVessel = new Map();

      // If no alarms are present return early.
      if (!alarms.length) {
         this._alarmsByVessel = updatedAlarmsByVessel;
         this.dispatchEvent(new Event(AlarmEvents.AlarmsUpdated));
         return;
      }

      for (let i = 0; i < alarms.length; i++) {
         const alarm = alarms[i];

         if (updatedAlarmsByVessel.has(alarm.imo)) {
               updatedAlarmsByVessel.get(alarm.imo)!.push(alarm);
         } else {
            updatedAlarmsByVessel.set(alarm.imo, new Array(alarm));
         }

         this._alarmsByVessel = updatedAlarmsByVessel;
      }

      this._rankVesselsByAlarmType();
      if(!sessionStorage.getItem("alarms_updated_at")){
         sessionStorage.setItem("alarms_updated_at", JSON.stringify(new Date()));
      }
      this.dispatchEvent(new Event(AlarmEvents.AlarmsUpdated));
   }
   removeDuplicateWeatherAlarms(alarms:FleetAlarm[]){
      let finalArray : FleetAlarm[] = []
      alarms.map((alarm:FleetAlarm)=>{
         if(alarm.alarmType !== AlarmType.HeavyWeather && alarm.alarmType !== AlarmType.ParametricRoll){
            finalArray.push(alarm)

         }
         else if(alarm.alarmType === AlarmType.HeavyWeather){
            let temp = finalArray.findIndex((el:FleetAlarm)=>el.imo === alarm.imo && el.alarmType === AlarmType.HeavyWeather)
            if(temp>-1){
               if(finalArray[temp].id < alarm.id){
                  finalArray[temp] = alarm;
               }
            }
            else{
               finalArray.push(alarm)
            }
         }
         else if(alarm.alarmType === AlarmType.ParametricRoll){
            let temp = finalArray.findIndex((el:FleetAlarm)=>el.imo === alarm.imo && el.alarmType === AlarmType.ParametricRoll)
            if(temp>-1){
               if(finalArray[temp].id < alarm.id){
                  finalArray[temp] = alarm;
               }
            }
            else{
               finalArray.push(alarm)
            }
         }
      })

      return finalArray
   }

   public get vesselRanks(): Map<number, VesselRank> {
      return this._vesselRanks;
   }

   public async getVesselAlarms(imo: number, excludeStates = [AlarmState.Ended, AlarmState.Pending, AlarmState.Cancelled], fromDate: string, toDate:string): Promise<FleetAlarm[] | undefined> {

      let includeDetections = true;
      if (!(JSON.stringify(excludeStates) == JSON.stringify([AlarmState.Ended, AlarmState.Pending, AlarmState.Cancelled]))) {
         includeDetections = false;
      }
      const alarms = await FleetAlarmsService.getAlarms(imo, true, includeDetections, excludeStates, undefined, undefined, fromDate,toDate);
      const fleetAlarms = await this._enrichAlarmLogsWithUsers(alarms)

      return fleetAlarms;
   }

   public getVesselAlarmsBasicInfo(imo:number) {
      return this._alarmsByVessel.get(imo) || []
   }

   // setting the disableCache flag to retrive the latest alarm
   public async getVesselAlarmByID(alarmId: number, disableCache = false): Promise<FleetAlarm> {
      const cachedAlarm: FleetAlarm | undefined = this._alarmByID.get(alarmId);

      if(cachedAlarm && !disableCache) {
         return cachedAlarm;
      }

      const alarm = await FleetAlarmsService.getAlarmByID(alarmId);
       const enrichedAlarms = await this._enrichAlarmLogsWithUsers([alarm])

      this._alarmByID.set(alarmId, enrichedAlarms[0]);
      const vesselWithAlarm = this._alarmsByVessel.get(alarm.imo);
      let updateAlarmForVeesel: Alarm[] = []
      if(vesselWithAlarm?.length) {
       updateAlarmForVeesel = Array.from(vesselWithAlarm!.values()).map(el => {
         if(el.id === alarm.id) {
            return {...alarm}
         }
         return el
      })
   }

      this._alarmsByVessel.set(alarm.imo, updateAlarmForVeesel);
      this._rankVesselsByAlarmType();
      this.dispatchEvent(new Event(AlarmEvents.AlarmsUpdated));

      return enrichedAlarms[0];
   }

   public async updateAlarm(alarmId: number, alarmState: AlarmState, reason: string, alarmNote?: string): Promise<FleetAlarm|null> {
      let updatedAlarm: FleetAlarm;

      try {
         await FleetAlarmsService!.updateAlarm(alarmId, alarmState, reason, alarmNote);
         updatedAlarm = await this.getVesselAlarmByID(alarmId, true);
         return updatedAlarm;
      } catch (err) {
         console.error(err)
         this.dispatchEvent(new Event(AlarmEvents.AlarmUpdateFailed));

         return null;
      }
   }

   public async updateWeatherAlarm(alarmId: number, alarmState: AlarmState, reason: string, alarmNote?: string): Promise<any> {
      let updatedAlarm: FleetAlarm;

      try {
         await FleetAlarmsService!.updateWeatherAlarm(alarmId, alarmState, reason, alarmNote);
         updatedAlarm = await this.getVesselAlarmByID(alarmId, true);
         return updatedAlarm;

      } catch (err:any) {
         err.response?.json().then((data:any) => {
            this.dispatchEvent(new CustomEvent(AlarmEvents.WeatherAlarmAcceptedByOtherVET, { detail: {
            err:data,
            alarmId: alarmId}}));
         })
         return null;
   
      }
   }


   private async _enrichAlarmLogsWithUsers(alarms: Alarm[]): Promise<FleetAlarm[]> {

      let fleetAlarms: FleetAlarm[] = []

         fleetAlarms = alarms.map(a => ({
            ...a,
            alarmLogs: a.alarmLogs ? a.alarmLogs.map(l => ({
               ...l,
            })):[]
         }));
      return fleetAlarms
   }


   public async getCompanyWeatherLimits(): Promise<any> {
      return await FleetAlarmsService.getCompanyWeatherLimits();
   }

   public async getCompanyWeatherLimitsHistory(isCustom:boolean): Promise<any> {
      return await FleetAlarmsService.getCompanyWeatherLimitsHistory(isCustom);
   }

   public async setCompanyWeatherLimits(payload:any): Promise<any> {
      return await FleetAlarmsService.setCompanyWeatherLimits(payload);
   }
    public async setCustomWeatherLimits(payload:any): Promise<any> {
      return await FleetAlarmsService.setCustomWeatherLimits(payload);
   }

   public async getCustomWeatherLimits(): Promise<any> {
       return await FleetAlarmsService.getCustomWeatherLimits() || [];
   }

   public async getCustomWeatherLimitsByIMO(imoList: number[]): Promise<any> {
      return await FleetAlarmsService.getCustomWeatherLimitsByIMO(imoList);
   }

   public async deleteCustomWeatherLimits(imo: number): Promise<any> {
      return await FleetAlarmsService.deleteCustomWeatherLimits(imo);
   }



}
